import GoogleIcon from '@assets/icons/google.svg?react';
import IconHide from '@assets/icons/hide.svg?react';
import MicrosoftIcon from '@assets/icons/microsoft.svg?react';
import IconShow from '@assets/icons/show.svg?react';
import LoginMedia from '@assets/login.svg?react';
import Logo from '@assets/logo.svg?react';
import LogoDark from '@assets/logo-dark.svg?react';
import {
 Box,
 Button,
 Checkbox,
 FormControlLabel,
 IconButton,
 Link as LinkMui,
 MenuItem,
 Select,
 TextField,
 Theme,
 Typography,
 useColorScheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import HeaderTags from '../components/common/HeaderTags';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { identityLoginStart } from '../redux/identity/identitySlice';
import { FormValues } from '../types/types';

function Login() {
 const navigate = useNavigate();
 const dispatch = useAppDispatch();
 const [passwordVisible, setPasswordVisible] = useState(false);
 const { mode } = useColorScheme();
 const {
  register,
  handleSubmit,
  watch,
  setValue,
  formState: { errors },
 } = useForm<FormValues>({
  defaultValues: {
   email: '',
   password: '',
  },
 });

 const success = useAppSelector((state) => state.identity.success);
 const loading = useAppSelector((state) => state.identity.loading);

 const onSubmit = (data: FormValues) => {
  dispatch(identityLoginStart({ email: data.email, password: data.password }));
 };

 useEffect(() => {
  if (success?.identityLogin) {
   navigate('/results');
  }
 }, [success]);

 return (
  <>
   <HeaderTags title="Results" description="Results" />
   <Box
    sx={{
     width: '100%',
     minHeight: '100%',
     display: 'flex',
     p: { xs: 2, md: 5 },
    }}
   >
    <Box
     sx={{
      width: '100%',
      display: 'flex',
      overflow: 'hidden',
      //   height: '100vh',
      background: (theme: Theme) => theme.palette.kemek.white,
      borderRadius: '20px',
     }}
    >
     <Box
      sx={{
       width: { xs: '100%', md: '50%' },
       display: 'flex',
       flexDirection: 'column',
       justifyContent: 'space-between',
       p: { xs: 2, md: 5 },
      }}
     >
      <Box
       sx={{
        display: 'flex',
        justifyContent: { xs: 'center', md: 'initial' },
        p: { xs: '12px', md: 0 },
        mb: { xs: 5, md: 0 },
       }}
      >
       {mode === 'light' && (
        <Link to="/">
         <Logo />
        </Link>
       )}

       {mode === 'dark' && (
        <Link to="/">
         <LogoDark />
        </Link>
       )}
      </Box>
      <Box
       component="form"
       onSubmit={handleSubmit(onSubmit)}
       sx={{
        width: { xs: '100%', md: '90%', lg: '70%', xl: '55%' },
        border: (theme: Theme) =>
         `1px solid ${theme.palette.kemek.lightestGray}`,
        borderRadius: '4px',
        margin: { xs: '0 auto 16px auto', md: '0 auto' },
        p: { xs: '20px', md: 5 },
       }}
      >
       <Box
        sx={{
         width: '100%',
         display: 'flex',
         justifyContent: 'flex-end',
         mb: 1,
        }}
       >
        <Select defaultValue="lt" size="small">
         <MenuItem value="lt">LT</MenuItem>
         <MenuItem value="en">EN</MenuItem>
        </Select>
       </Box>
       <Typography sx={{ fontSize: 18, mb: '12px' }}>
        Get started now
       </Typography>
       <Typography sx={{ fontSize: 14, mb: '28px' }}>
        Enter your credentials to access your account
       </Typography>
       <TextField
        {...register('email', { required: 'This field is required' })}
        label="Email"
        sx={{
         width: '100%',
         mb: '12px',
         //  background: customColors.lightBlue,
         //  '& fieldset': {
         //   border: 0,
         //  },
        }}
        slotProps={{
         inputLabel: {
          shrink: watch('email')?.length > 0,
         },
        }}
        error={!!errors.email}
        helperText={errors.email?.message?.toString() || ''}
       />
       <Box sx={{ position: 'relative', mb: { xs: 2, md: '24px' } }}>
        <TextField
         {...register('password', { required: 'This field is required' })}
         type={passwordVisible ? 'text' : 'password'}
         label="Password"
         sx={{
          width: '100%',
          //  background: customColors.lightBlue,
          //  '& fieldset': {
          //   border: 0,
          //  },
         }}
         slotProps={{
          inputLabel: {
           shrink: watch('password')?.length > 0,
          },
         }}
         error={!!errors.password}
         helperText={errors.password?.message?.toString() || ''}
        />
        <IconButton
         onClick={() => setPasswordVisible(!passwordVisible)}
         sx={{
          position: 'absolute',
          top: '27px',
          right: '12px',
          transform: 'translateY(-50%)',
          opacity: 0.5,
         }}
        >
         {passwordVisible ? <IconHide /> : <IconShow />}
        </IconButton>
       </Box>
       <Box
        sx={{
         display: 'flex',
         alignItems: { xs: 'center', md: 'flex-start', lg: 'center' },
         flexDirection: { md: 'column', lg: 'row' },
         justifyContent: 'space-between',
         gap: 1,
         mb: { xs: 2, md: 3 },
         fontSize: 12,
        }}
       >
        <FormControlLabel
         sx={{}}
         label={<Typography sx={{ fontSize: 12 }}>Remember me</Typography>}
         control={<Checkbox checked color="primary" onChange={() => {}} />}
        />
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <LinkMui
         onClick={() => {
          if (import.meta.env.DEV) {
           setValue('email', 'daniil.mikenas@gmail.com');
           setValue('password', 'Ux6kUQjzrZHxAg1d');
          }
         }}
        >
         Forgot password?
        </LinkMui>
       </Box>

       <Button
        type="submit"
        variant="contained"
        sx={{ width: '100%', mb: { xs: 4, md: 5 } }}
        disabled={loading?.identityLogin || false}
       >
        Login
       </Button>
       <Typography sx={{ fontSize: 14, mb: 3 }}>
        Use another service to log in
       </Typography>
       <Box
        sx={{
         width: '100%',
         display: 'flex',
         justifyContent: 'center',
         flexDirection: { xs: 'column', lg: 'row' },
         gap: 1,
        }}
       >
        <Button
         variant="contained"
         color="secondary"
         disableElevation
         startIcon={<GoogleIcon />}
         sx={{
          background: (theme: Theme) => theme.palette.kemek.lightBlue,
          color: (theme: Theme) => theme.palette.kemek.darkGray,
          fontSize: 10,
         }}
        >
         Login with Google
        </Button>
        <Button
         variant="contained"
         color="secondary"
         disableElevation
         startIcon={<MicrosoftIcon />}
         sx={{
          background: (theme: Theme) => theme.palette.kemek.lightBlue,
          color: (theme: Theme) => theme.palette.kemek.darkGray,
          fontSize: 10,
         }}
        >
         Login with Microsoft
        </Button>
       </Box>
      </Box>
      <Box
       sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        fontSize: 12,
        color: (theme: Theme) => theme.palette.kemek.darkGray,
       }}
      >
       © {new Date().getFullYear()} Scale Expert. All rights reserved
      </Box>
     </Box>
     <Box
      sx={{
       width: '50%',
       color: 'white',
       p: '75px',
       background: (theme: Theme) => theme.palette.kemek.blue,
       display: { xs: 'none', md: 'flex' },
       flexDirection: 'column',
       justifyContent: 'center',
       //    alignItems: 'center',
      }}
     >
      <Typography sx={{ mb: '160px', ml: '45px', fontSize: 36, width: 360 }}>
       Efficient weighing, easy access
      </Typography>
      <LoginMedia />
     </Box>
    </Box>
   </Box>
  </>
 );
}
export default Login;
